import { tenantAwareApiWrapper, withOrchestratorClient } from './portal-http';
import { signalsClient } from '@terra/shared-rest-clients-signals';
import type {
  SignalsContract,
  GroupedSignalsSearchBodySchema,
  SignalsCountByAssetQuerySchema,
  SignalsSearchByApplicationBodySchema,
  SignalsSearchQuerySchema,
} from '@terra/shared-contracts-signals';
import {
  normalizeGroupedSignal,
  toSignalDto,
  type SignalDto,
  type SignalListGroupByField,
  type SignalUpdateInput,
  type SignalsBodyDto,
  type SignalsSearchQueryDto,
} from '@terra/shared-contracts-signals';
import type { z } from 'zod';
import {
  type AiComponentTypeType,
  type BlockedReasonType,
  type ConfirmedVulnerabilityFindingStateType,
  type SignalCategoryType,
  type SignalOriginType,
  type SignalStageType,
  type SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import { type ConfidenceLevelType } from '../../../types/confidence';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';

export const portalSignalsApi = {
  getSignalsByApplication: async (
    applicationId: string,
    options: {
      pagination?: {
        skip?: number;
        limit?: number;
        orderBy?: { field: string; order: 'asc' | 'desc' }[];
      };
      filters?: Record<string, unknown>;
      endpointIds?: string[];
      assetIds?: string[];
      componentTypes?: AiComponentTypeType[];
      urlPatterns?: string[];
      includeApplicationLevelGroup?: boolean;
      groupBy?: SignalListGroupByField;
    } = {},
  ) => {
    const result = await tenantAwareApiWrapper<
      typeof SignalsContract.searchSignalsByApplicationId
    >({
      endpointFn: (headers) => {
        const body = {
          ...(options.filters || {}),
          skip: options.pagination?.skip,
          limit: options.pagination?.limit,
          orderBy: options.pagination?.orderBy,
          ...(options.endpointIds?.length && {
            endpointIds: options.endpointIds,
          }),
          ...(options.assetIds?.length && {
            assetIds: options.assetIds,
          }),
          ...(options.componentTypes !== undefined && {
            componentTypes: options.componentTypes,
          }),
          ...(options.urlPatterns?.length && {
            urlPatterns: options.urlPatterns,
          }),
          ...(options.includeApplicationLevelGroup && {
            includeApplicationLevelGroup: true,
          }),
          ...(options.groupBy && { groupBy: options.groupBy }),
        };

        return signalsClient.searchSignalsByApplicationId({
          params: { applicationId },
          body: body as z.input<typeof SignalsSearchByApplicationBodySchema>,
          ...withOrchestratorClient(headers),
        });
      },
    });

    return {
      ...result,
      signals: result.signals.map(normalizeGroupedSignal),
    };
  },

  getGroupedSignalsByApplication: (
    applicationId: string,
    filters?: Record<string, unknown> & {
      skip?: number;
      limit?: number;
      orderBy?: { field: string; order: 'asc' | 'desc' }[];
      urlPatterns?: string[];
      endpointIds?: string[];
      signalsPerGroup?: number;
      includeApplicationLevelGroup?: boolean;
    },
  ) =>
    tenantAwareApiWrapper<
      typeof SignalsContract.getGroupedSignalsByApplicationId
    >({
      endpointFn: (headers) => {
        return signalsClient.getGroupedSignalsByApplicationId({
          params: { applicationId },
          query: (filters || {}) as z.input<
            typeof GroupedSignalsSearchBodySchema
          >,
          ...withOrchestratorClient(headers),
        });
      },
    }),

  getSignal: async (signalId: string): Promise<SignalDto> => {
    const signal = await tenantAwareApiWrapper<
      typeof SignalsContract.getSignal
    >({
      endpointFn: (headers) => {
        return signalsClient.getSignal({
          params: { id: signalId },
          ...withOrchestratorClient(headers),
        });
      },
    });

    return toSignalDto(signal);
  },

  getSignalsByEndpoint: (
    applicationId: string,
    endpointId: string,
    filters?: Pick<
      SignalsSearchQueryDto,
      | 'skip'
      | 'limit'
      | 'description'
      | 'confidence'
      | 'category'
      | 'assigneeId'
      | 'url'
      | 'orderBy'
      | 'hasExecution'
      | 'hasSecurityTestCase'
      | 'createdStartDate'
      | 'createdEndDate'
      | 'testedStartDate'
      | 'testedEndDate'
    > & {
      hasFinding?: boolean;
      signalsPerGroup?: number;
      urlPatterns?: string[];
    },
  ) =>
    tenantAwareApiWrapper<
      typeof SignalsContract.getGroupedSignalsByApplicationId
    >({
      endpointFn: (headers) => {
        const query = {
          endpointIds: [endpointId],
          skip: filters?.skip ?? 0,
          limit: filters?.limit ?? 100,
          ...(filters?.description && { description: filters.description }),
          ...(filters?.confidence && { confidence: filters.confidence }),
          ...(filters?.category && { category: filters.category }),
          ...(filters?.assigneeId && { assigneeId: filters.assigneeId }),
          ...(filters?.url && { url: filters.url }),
          ...(filters?.hasExecution !== undefined && {
            hasExecution: filters.hasExecution,
          }),
          ...(filters?.hasFinding !== undefined && {
            hasFinding: filters.hasFinding,
          }),
          ...(filters?.hasSecurityTestCase !== undefined && {
            hasSecurityTestCase: filters.hasSecurityTestCase,
          }),
          ...(filters?.createdStartDate && {
            createdStartDate: filters.createdStartDate,
          }),
          ...(filters?.createdEndDate && {
            createdEndDate: filters.createdEndDate,
          }),
          ...(filters?.testedStartDate && {
            testedStartDate: filters.testedStartDate,
          }),
          ...(filters?.testedEndDate && {
            testedEndDate: filters.testedEndDate,
          }),
          ...(filters?.orderBy && { orderBy: filters.orderBy }),
          ...(filters?.signalsPerGroup && {
            signalsPerGroup: filters.signalsPerGroup,
          }),
          ...(filters?.urlPatterns && { urlPatterns: filters.urlPatterns }),
        };

        return signalsClient.getGroupedSignalsByApplicationId({
          params: { applicationId },
          query: query as z.input<typeof GroupedSignalsSearchBodySchema>,
          ...withOrchestratorClient(headers),
        });
      },
    }),

  getSignalsCountByApplicationId: (
    applicationId: string,
    endpointId?: string,
    filters?: {
      stage?: SignalStageType[];
      status?: SignalStatusType[];
      confirmedVulnerabilityFindingState?: ConfirmedVulnerabilityFindingStateType[];
      blockedReason?: BlockedReasonType[];
      description?: string;
      confidence?: ConfidenceLevelType[];
      category?: SignalCategoryType[];
      origin?: SignalOriginType[];
      source?: SignalsSearchQueryDto['source'];
      url?: string;
      assigneeId?: string[];
      hasExecution?: boolean;
      hasFinding?: boolean;
      hasSecurityTestCase?: boolean;
      isAppWide?: boolean;
      createdStartDate?: Date | null;
      createdEndDate?: Date | null;
      testedStartDate?: Date | null;
      testedEndDate?: Date | null;
      urlPatterns?: string[];
      endpointIds?: string[];
      assetIds?: string[];
      componentTypes?: AiComponentTypeType[];
      rejectedOnly?: boolean;
      lifecycleState?: LifecycleStateFilterDto;
    },
    includeApplicationLevelGroup?: boolean,
  ) =>
    tenantAwareApiWrapper<
      typeof SignalsContract.searchSignalsCountByApplicationId
    >({
      endpointFn: (headers) => {
        const endpointIds =
          endpointId != null
            ? [endpointId]
            : filters?.endpointIds?.length
              ? filters.endpointIds
              : undefined;

        const body = {
          ...(endpointIds && { endpointIds }),
          ...(filters?.assetIds?.length && { assetIds: filters.assetIds }),
          ...(filters?.componentTypes !== undefined && {
            componentTypes: filters.componentTypes,
          }),
          ...(filters?.urlPatterns && { urlPatterns: filters.urlPatterns }),
          ...(filters?.stage?.length && { stage: filters.stage }),
          ...(filters?.status?.length && { status: filters.status }),
          ...(filters?.confirmedVulnerabilityFindingState?.length && {
            confirmedVulnerabilityFindingState:
              filters.confirmedVulnerabilityFindingState,
          }),
          ...(filters?.blockedReason?.length && {
            blockedReason: filters.blockedReason,
          }),
          ...(filters?.description && { description: filters.description }),
          ...(filters?.confidence?.length && {
            confidence: filters.confidence,
          }),
          ...(filters?.category && { category: filters.category }),
          ...(filters?.origin && { origin: filters.origin }),
          ...(filters?.source?.length && { source: filters.source }),
          ...(filters?.url && { url: filters.url }),
          ...(filters?.assigneeId?.length && {
            assigneeId: filters.assigneeId,
          }),
          ...(filters?.hasExecution !== undefined && {
            hasExecution: filters.hasExecution,
          }),
          ...(filters?.hasFinding !== undefined && {
            hasFinding: filters.hasFinding,
          }),
          ...(filters?.hasSecurityTestCase !== undefined && {
            hasSecurityTestCase: filters.hasSecurityTestCase,
          }),
          ...(filters?.isAppWide !== undefined && {
            isAppWide: filters.isAppWide,
          }),
          ...(filters?.createdStartDate && {
            createdStartDate: filters.createdStartDate,
          }),
          ...(filters?.createdEndDate && {
            createdEndDate: filters.createdEndDate,
          }),
          ...(filters?.testedStartDate && {
            testedStartDate: filters.testedStartDate,
          }),
          ...(filters?.testedEndDate && {
            testedEndDate: filters.testedEndDate,
          }),
          ...(includeApplicationLevelGroup && {
            includeApplicationLevelGroup: true,
          }),
          ...(filters?.rejectedOnly && { rejectedOnly: true }),
          ...(filters?.lifecycleState && {
            lifecycleState: filters.lifecycleState,
          }),
        };

        return signalsClient.searchSignalsCountByApplicationId({
          params: { applicationId },
          body: body as z.input<typeof SignalsSearchByApplicationBodySchema>,
          ...withOrchestratorClient(headers),
        });
      },
    }),

  getSignalsCountByEndpoint: (
    applicationId: string,
    filters?: Pick<
      SignalsSearchQueryDto,
      | 'description'
      | 'entryPointId'
      | 'securityTestCaseId'
      | 'confidence'
      | 'category'
      | 'url'
      | 'assigneeId'
      | 'endpointId'
      | 'hasExecution'
      | 'hasFinding'
      | 'lifecycleState'
    >,
    urlPatterns?: string[],
    includeApplicationLevelGroup?: boolean,
  ) =>
    tenantAwareApiWrapper<typeof SignalsContract.getSignalsCountByEndpoint>({
      endpointFn: (headers) => {
        return signalsClient.getSignalsCountByEndpoint({
          params: { applicationId },
          query: {
            ...filters,
            urlPatterns,
            includeApplicationLevelGroup,
          } as z.input<typeof SignalsSearchQuerySchema>,
          ...withOrchestratorClient(headers),
        });
      },
    }),

  getSignalsCountByAssetId: (
    applicationId: string,
    query: z.input<typeof SignalsCountByAssetQuerySchema> = {},
  ) =>
    tenantAwareApiWrapper<typeof SignalsContract.getSignalsCountByAssetId>({
      endpointFn: (headers) => {
        return signalsClient.getSignalsCountByAssetId({
          params: { applicationId },
          query,
          ...withOrchestratorClient(headers),
        });
      },
    }),

  getCategoryDistributionByApplicationId: (applicationId: string) =>
    tenantAwareApiWrapper<
      typeof SignalsContract.getCategoryDistributionByApplicationId
    >({
      endpointFn: (headers) => {
        return signalsClient.getCategoryDistributionByApplicationId({
          params: { applicationId },
          query: {},
          ...withOrchestratorClient(headers),
        });
      },
    }),

  updateSignalRank: (
    signalId: string,
    rank: 'APPROVED' | 'REJECTED' | 'UNRANKED',
  ) =>
    tenantAwareApiWrapper<typeof SignalsContract.updateSignal>({
      endpointFn: (headers) =>
        signalsClient.updateSignal({
          params: { id: signalId },
          body: { rank },
          ...withOrchestratorClient(headers),
        }),
    }),

  updateSignalsBulk: (
    updates: Array<{
      id: string;
      update: SignalUpdateInput;
    }>,
  ) =>
    tenantAwareApiWrapper<typeof SignalsContract.updateSignalsBulk>({
      endpointFn: (headers) => {
        return signalsClient.updateSignalsBulk({
          body: {
            updates: updates.map(({ id, update }) => ({
              id,
              update,
            })),
          },
          ...withOrchestratorClient(headers),
        });
      },
    }),

  deleteSignalsBulk: (ids: string[]) =>
    tenantAwareApiWrapper<typeof SignalsContract.deleteSignalsBulk>({
      endpointFn: (headers) => {
        return signalsClient.deleteSignalsBulk({
          body: { ids },
          ...withOrchestratorClient(headers),
        });
      },
    }),

  createSignal: (body: SignalsBodyDto) =>
    tenantAwareApiWrapper<typeof SignalsContract.createSignal>({
      endpointFn: (headers) =>
        signalsClient.createSignal({
          body,
          ...withOrchestratorClient(headers),
        }),
    }),

  updateSignal: (signalId: string, update: SignalUpdateInput) =>
    tenantAwareApiWrapper<typeof SignalsContract.updateSignal>({
      endpointFn: (headers) =>
        signalsClient.updateSignal({
          params: { id: signalId },
          body: update,
          ...withOrchestratorClient(headers),
        }),
    }),

  getSignalAuditLogs: (signalId: string, skip = 0, limit = 50) =>
    tenantAwareApiWrapper<typeof SignalsContract.getAuditLogs>({
      endpointFn: (headers) =>
        signalsClient.getAuditLogs({
          params: { id: signalId },
          query: { skip, limit },
          ...withOrchestratorClient(headers),
        }),
    }),

  // Reports endpoints
};
